/* AMOR product cards: the photo strip (8.10.2026, "wariant 1"; markup in snippets/am-card, styles in amor-cards.css).
   The photos of a card sit side by side in a scroll-snap strip: a finger swipes one photo at a time and the strip
   always stops on a whole photo. This script keeps the segments under the photo in step with the strip, flips the
   photos under a mouse (left part of the photo: first photo, then the next ones; back to the first when the
   pointer leaves) and loads the photos after the first, which are not in the page up front (data-src):
   on a touch screen the second one when the card is in view and the rest on the first touch, with a mouse all
   of them when the pointer enters. Without this script the card shows its first photo and does not swipe.
   Clarity (snippets/am-clarity): a swipe to another photo sends "karta_zdjecia_przewijanie", the event the card
   arrows used to send, so the numbers stay comparable; flipping with the mouse sends "karta_zdjecia_mysz" once
   per page.
   10.10.2026: a click anywhere on a card that is not a link or button opens the product (see the end). */
(() => {
  document.documentElement.classList.add('am-cards-js');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const load = (img) => {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  };
  const loadAll = (track) => track.querySelectorAll('img[data-src]').forEach(load);
  let mouseSent = false;
  const paint = (track) => {
    const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (i !== (track.amIdx || 0)) {
      track.amIdx = i;
      const cl = window.amClarity;
      if (cl && performance.now() - (track.amSet || 0) > 250) cl.event('karta_zdjecia_przewijanie');
      else if (cl && !mouseSent) { mouseSent = true; cl.event('karta_zdjecia_mysz'); }
    }
    const card = track.closest('.am-card');
    const segs = card ? card.querySelectorAll('.am-card__segs i') : [];
    segs.forEach((s, k) => s.classList.toggle('is-on', k === i));
  };
  const show = (track, i) => {
    const x = i * track.clientWidth;
    if (Math.abs(track.scrollLeft - x) > 1) { track.amSet = performance.now(); track.scrollLeft = x; }
  };

  /* Segments follow the strip. Scroll events do not bubble, hence the capture listener. */
  let raf = 0;
  const moved = new Set();
  document.addEventListener('scroll', (e) => {
    const t = e.target;
    if (!t.classList || !t.classList.contains('am-card__track')) return;
    moved.add(t);
    if (!raf) raf = requestAnimationFrame(() => { raf = 0; moved.forEach(paint); moved.clear(); });
  }, { capture: true, passive: true });

  /* Touch: everything in the strip starts loading when a finger lands on it. */
  document.addEventListener('touchstart', (e) => {
    const t = e.target.closest && e.target.closest('.am-card__track');
    if (t) loadAll(t);
  }, { capture: true, passive: true });

  /* Touch screens: the second photo loads once half of a card's photo is on screen, so the first swipe does not
     land on an empty tile. Not in rails, where the photo does not swipe.
     15.10.2026: the watching starts at the first touch, scroll or key, or 2.5 s after "load", not straight away:
     the second photos of the cards on the first screen (25-80 KB each) were fetched together with the first
     screen and its main photo. A finger on a card still loads its whole strip at once (touchstart above). */
  if (!fine.matches && 'IntersectionObserver' in window) {
    const kick = ['touchstart', 'scroll', 'pointerdown', 'keydown'];
    let watching = false;
    const watch = () => {
      if (watching) return;
      watching = true;
      kick.forEach((n) => removeEventListener(n, watch, true));
      const io = new IntersectionObserver((rows) => rows.forEach((r) => {
        if (!r.isIntersecting) return;
        io.unobserve(r.target);
        load(r.target.querySelector('img[data-src]'));
      }), { threshold: 0.5 });
      document.querySelectorAll('.am-card__media[data-am-gal]').forEach((m) => { if (!m.closest('.am-slider')) io.observe(m); });
    };
    kick.forEach((n) => addEventListener(n, watch, { capture: true, passive: true }));
    const later = () => setTimeout(watch, 2500);
    if (document.readyState === 'complete') later(); else addEventListener('load', later, { once: true });
  }

  /* Mouse: the photo follows the pointer across the card. */
  let cur = null;
  const leave = () => { if (cur) { show(cur, 0); cur = null; } };
  document.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const media = e.target.closest && e.target.closest('.am-card__media[data-am-gal]');
    const track = media && media.querySelector('.am-card__track');
    if (cur && cur !== track) leave();
    if (!track) return;
    if (cur !== track) { cur = track; loadAll(track); }
    const r = media.getBoundingClientRect();
    const n = track.children.length;
    show(track, Math.min(n - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * n))));
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse' || !cur) return;
    const media = cur.closest('.am-card__media');
    if (!e.relatedTarget || !media.contains(e.relatedTarget)) leave();
  }, { passive: true });

  /* The whole card opens the product (10.10.2026). Clarity showed dead clicks on the parts of a card that were
     not links: the segments under the photo, the badges, dimensions, colours, price and delivery line. A click
     there now does what a click on the name does (that same link is clicked, so "karta_produktu_klik" still
     counts). Links, buttons and form fields inside the card keep their own job: "Dodaj do koszyka" adds to the
     cart, "Zobacz produkt" opens the product. Nothing happens while text is being selected. Ctrl/Cmd/Shift-click
     and the middle button open the product in a new tab, as on a link. A drag in a [data-am-drag] row cancels
     its click before it gets here. */
  const SKIP = 'a, button, input, select, textarea, label, summary, form, [role="button"]';
  const cardLink = (e) => {
    const card = e.target.closest && e.target.closest('.am-card');
    if (!card) return null;
    const own = e.target.closest(SKIP);
    if (own && card.contains(own)) return null;
    if (window.getSelection && String(window.getSelection()).trim()) return null;
    return card.querySelector('.am-card__name a');
  };
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    const a = cardLink(e);
    if (!a) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey) window.open(a.href, '_blank', 'noopener');
    else a.click();
  });
  document.addEventListener('auxclick', (e) => {
    if (e.defaultPrevented || e.button !== 1) return;
    const a = cardLink(e);
    if (a) window.open(a.href, '_blank', 'noopener');
  });
  const css = document.createElement('style');
  css.textContent = '.am-card{cursor:pointer}';
  document.head.appendChild(css);
})();